/**
 * Template introspection helpers — parse a Meta template row (with its raw
 * `components[]`) into the bits the send-time UIs need: id, body text, header
 * format, buttons, component presence, and body-variable detection.
 *
 * Extracted from the copies that were duplicated across SendTemplateModal,
 * campaigns.new, and campaigns.$uid.edit so the bot flow builder (and those
 * three call sites, refactored later) share one implementation.
 */

/** A Meta template row as returned by `trpc.whatsapp.templates.list`. */
export interface RawTemplate {
  id?: number | string | null;
  name?: string;
  language?: string;
  status?: string;
  components?: Array<{
    type?: string;
    format?: string;
    text?: string;
    buttons?: Array<{ type?: string; url?: string; text?: string }>;
  }>;
}

export function extractId(t: { id?: number | string | null }): number | null {
  const idValue = t.id;
  if (typeof idValue === 'number') return idValue;
  if (typeof idValue === 'string') {
    const n = Number(idValue);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function extractBodyText(t: unknown): string | null {
  if (!t || typeof t !== 'object') return null;
  const obj = t as { components?: Array<{ type?: string; text?: string }> };
  return obj.components?.find((c) => (c.type ?? '').toUpperCase() === 'BODY')?.text ?? null;
}

export function extractHeaderFormat(t: unknown): string | null {
  if (!t || typeof t !== 'object') return null;
  const obj = t as { components?: Array<{ type?: string; format?: string }> };
  return obj.components?.find((c) => (c.type ?? '').toUpperCase() === 'HEADER')?.format ?? null;
}

export function extractButtons(t: unknown): Array<{ type: string; url?: string; text?: string }> {
  if (!t || typeof t !== 'object') return [];
  const obj = t as {
    components?: Array<{
      type?: string;
      buttons?: Array<{ type?: string; url?: string; text?: string }>;
    }>;
  };
  const buttons = obj.components?.find((c) => (c.type ?? '').toUpperCase() === 'BUTTONS');
  return (buttons?.buttons ?? []).map((b) => ({
    type: (b.type ?? '').toUpperCase(),
    url: b.url,
    text: b.text,
  }));
}

export function hasComponent(t: unknown, type: string): boolean {
  if (!t || typeof t !== 'object') return false;
  const obj = t as { components?: Array<{ type?: string }> };
  return (obj.components ?? []).some((c) => (c.type ?? '').toUpperCase() === type.toUpperCase());
}

export interface RawCarouselCard {
  headerFormat: 'IMAGE' | 'VIDEO';
  /** Raw body text with `{{1}}`/`{{2}}` placeholders, not yet substituted. */
  bodyText?: string;
  buttons?: Array<{ type: string; text?: string }>;
}

/**
 * Parses a carousel template's raw `components[]` into per-card render data
 * (header format, raw body text, buttons) — the render-time counterpart of
 * `CarouselSendEditor.detectCarouselCards`, which only counts body variables
 * for building an input form rather than keeping the actual copy.
 */
export function extractCarouselCards(t: unknown): RawCarouselCard[] | null {
  if (!t || typeof t !== 'object') return null;
  const obj = t as {
    components?: Array<{
      type?: string;
      cards?: Array<{
        components?: Array<{
          type?: string;
          format?: string;
          text?: string;
          buttons?: Array<{ type?: string; text?: string }>;
        }>;
      }>;
    }>;
  };
  const carousel = obj.components?.find((c) => (c.type ?? '').toUpperCase() === 'CAROUSEL');
  if (!carousel?.cards) return null;
  return carousel.cards.map((card) => {
    const comps = card.components ?? [];
    const header = comps.find((c) => (c.type ?? '').toUpperCase() === 'HEADER');
    const body = comps.find((c) => (c.type ?? '').toUpperCase() === 'BODY');
    const buttons = comps.find((c) => (c.type ?? '').toUpperCase() === 'BUTTONS');
    return {
      headerFormat: ((header?.format ?? 'IMAGE').toUpperCase() as 'IMAGE' | 'VIDEO') || 'IMAGE',
      bodyText: body?.text,
      buttons: (buttons?.buttons ?? []).map((b) => ({
        type: (b.type ?? '').toUpperCase(),
        text: b.text,
      })),
    };
  });
}

export interface DetectedBodyVariables {
  /** POSITIONAL uses {{1}}..{{N}}; NAMED uses {{first_name}} etc. */
  format: 'positional' | 'named';
  /** For positional: ['1','2',…]; for named: the variable names in order. */
  names: string[];
}

/**
 * Detect a template body's variable slots. Positional `{{1}}` placeholders
 * take precedence; if none are present, named `{{first_name}}` placeholders
 * are collected. Deduped, in first-seen order.
 */
export function detectBodyVariables(bodyText: string | null | undefined): DetectedBodyVariables {
  if (!bodyText) return { format: 'positional', names: [] };
  const positional = new Set<string>();
  for (const m of bodyText.matchAll(/\{\{(\d+)\}\}/g)) positional.add(m[1]!);
  if (positional.size > 0) {
    return {
      format: 'positional',
      names: [...positional].sort((a, b) => Number(a) - Number(b)),
    };
  }
  const named = new Set<string>();
  for (const m of bodyText.matchAll(/\{\{([a-z][a-z0-9_]*)\}\}/g)) named.add(m[1]!);
  // No variables at all → neutral positional default (an empty body isn't
  // "named"). Only a body that actually contains named placeholders is NAMED.
  if (named.size === 0) return { format: 'positional', names: [] };
  return { format: 'named', names: [...named] };
}
